/**
 * Graph Validation Utilities
 *
 * Provides data validation for react-d3-graph to prevent crashes from invalid links.
 */

export interface GraphNode {
  id: string;
  [key: string]: any;
}

export interface GraphLink {
  source: string;
  target: string;
  [key: string]: any;
}

export interface GraphData {
  nodes: GraphNode[];
  links: GraphLink[];
}

/**
 * Validates and sanitizes graph data by filtering out invalid links.
 *
 * A link is considered invalid if either its source or target node ID
 * does not exist in the nodes array. This prevents react-d3-graph from
 * crashing with "invalid links data structure" errors.
 *
 * @param data - The graph data to validate
 * @returns Sanitized graph data with only valid links
 */
export function validateGraphData<
  N extends GraphNode,
  L extends GraphLink,
>(data: { nodes: N[]; links: L[] }): { nodes: N[]; links: L[] } {
  if (!data || !data.nodes || !data.links) {
    return { nodes: [], links: [] };
  }

  // Create a Set of all valid node IDs for O(1) lookup
  const nodeIds = new Set(data.nodes.map(node => node.id));

  // Track invalid links for detailed logging
  const invalidLinks: Array<{
    link: GraphLink;
    missingSource: boolean;
    missingTarget: boolean;
  }> = [];

  // Filter links - only keep links where BOTH source AND target exist,
  // and dedupe by source,target pair (react-d3-graph keys link elements by
  // `${source},${target}`, so duplicates produce React duplicate-key warnings).
  // Also collapses reverse pairs (A→B and B→A) since the underlying graph is
  // undirected for our purposes.
  const seenPairs = new Set<string>();
  let duplicateCount = 0;
  const validLinks = data.links.filter(link => {
    const hasValidSource = nodeIds.has(link.source);
    const hasValidTarget = nodeIds.has(link.target);

    if (!hasValidSource || !hasValidTarget) {
      invalidLinks.push({
        link,
        missingSource: !hasValidSource,
        missingTarget: !hasValidTarget,
      });
      return false;
    }

    const [a, b] = [link.source, link.target].sort();
    const pairKey = `${a}::${b}`;
    if (seenPairs.has(pairKey)) {
      duplicateCount++;
      return false;
    }
    seenPairs.add(pairKey);
    return true;
  });

  if (duplicateCount > 0) {
    console.warn(
      `[Graph Validation] Dropped ${duplicateCount} duplicate link(s).`
    );
  }

  if (invalidLinks.length > 0) {
    const missingNodeIds = new Set<string>();
    invalidLinks.forEach(({ link, missingSource, missingTarget }) => {
      if (missingSource) missingNodeIds.add(link.source);
      if (missingTarget) missingNodeIds.add(link.target);
    });
    console.warn(
      `[Graph Validation] Dropped ${invalidLinks.length} orphan link(s) referencing ${missingNodeIds.size} missing node ID(s):`,
      Array.from(missingNodeIds)
    );
  }

  return {
    nodes: data.nodes,
    links: validLinks,
  };
}
